import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Context as IntlContext } from "context/Intl";
import { InfiniteListContext } from "context/InfiniteList/Context";
import useQueryString from "helpers/useQueryString";
import { fetchAutocompleteAddress } from "hooks/autocomplete";
import { useAdCategories } from "hooks/reference/adCategory";
import type { HttpResponse } from "lib/fetch";
import { AdFrequency } from "types/ad";
import {
    POSTED_PERIOD_ALL,
    POSTED_PERIOD_LAST_24H,
    POSTED_PERIOD_LAST_MONTH,
    POSTED_PERIOD_LAST_WEEK,
    RADIUS_ALL,
} from "components/ListPage/filters/inlineFilterConfig";
import type { CityFeature, CityOption, FilterOption } from "components/ListPage/filters";
import {
    buildResetInlineAdFilterSearchParams,
    buildSearchParams,
    getSingleQueryValue,
    hasActiveInlineAdFilters,
    joinCsv,
    splitCsv,
} from "./searchParams";

export const useInlineAdFilters = () => {
    const intl = useContext(IntlContext);
    const { pager, activeFilters, changeFetcherParams } = useContext(InfiniteListContext);
    const { values: queryValues, setAll } = useQueryString();
    const adCategories = useAdCategories();

    const selectedCategories = useMemo(
        () => splitCsv(queryValues.filter_categories ?? activeFilters?.filter_categories),
        [activeFilters?.filter_categories, queryValues.filter_categories]
    );
    const selectedSubcategories = useMemo(
        () => splitCsv(queryValues.filter_subcategories ?? activeFilters?.filter_subcategories),
        [activeFilters?.filter_subcategories, queryValues.filter_subcategories]
    );
    const selectedFrequenciesFromParams = useMemo(
        () => splitCsv(queryValues.filter_frequencies ?? activeFilters?.filter_frequencies),
        [activeFilters?.filter_frequencies, queryValues.filter_frequencies]
    );
    const selectedFrequencies = useMemo(
        () => (selectedFrequenciesFromParams.length === 1 ? selectedFrequenciesFromParams : []),
        [selectedFrequenciesFromParams]
    );
    const selectedPostedPeriod = getSingleQueryValue(
        queryValues.filter_posted_period ?? activeFilters?.filter_posted_period
    );
    const selectedCity = getSingleQueryValue(queryValues.filter_city ?? activeFilters?.filter_city);
    const selectedRadius = getSingleQueryValue(queryValues.filter_range ?? activeFilters?.filter_range);
    const currentKeyword = getSingleQueryValue(queryValues.filter_keyword).trim();
    const selectedCoordinatesLat = getSingleQueryValue(
        queryValues.filter_coordinates_lat ?? activeFilters?.filter_coordinates_lat
    );
    const selectedCoordinatesLon = getSingleQueryValue(
        queryValues.filter_coordinates_lon ?? activeFilters?.filter_coordinates_lon
    );
    const hasSelectedCityCoordinates = selectedCoordinatesLat !== "" && selectedCoordinatesLon !== "";
    const canUseRadius = hasSelectedCityCoordinates;
    const [cityInput, setCityInput] = useState(selectedCity);
    const [cityOptions, setCityOptions] = useState<CityOption[]>([]);
    const activeFilterState = useMemo(
        () => ({ ...activeFilters, ...queryValues }),
        [activeFilters, queryValues]
    );
    const hasInlineAdFilters = hasActiveInlineAdFilters(activeFilterState);

    const categories = useMemo(() => adCategories.data ?? [], [adCategories.data]);
    const categoryOptions = useMemo<FilterOption[]>(
        () =>
            categories.map((category) => ({
                value: category.value,
                label: String(intl.formatMessage(`category.${category.value.toLowerCase()}`)),
            })),
        [categories, intl]
    );

    const selectedCategorySet = useMemo(() => new Set(selectedCategories), [selectedCategories]);
    const subcategoryOptions = useMemo<FilterOption[]>(
        () =>
            categories
                .filter((category) => selectedCategorySet.size === 0 || selectedCategorySet.has(category.value))
                .flatMap((category) =>
                    category.subcategories.map((subcategory) => ({
                        value: subcategory.value,
                        label: String(
                            intl.formatMessage(
                                `subcategory.${category.value.toLowerCase()}.${subcategory.value.toLowerCase()}.label`
                            )
                        ),
                    }))
                ),
        [categories, intl, selectedCategorySet]
    );

    const displayedSubcategories = useMemo(() => {
        const validSubcategoryValues = new Set(subcategoryOptions.map((subcategory) => subcategory.value));
        return selectedSubcategories.filter((subcategory) => validSubcategoryValues.has(subcategory));
    }, [selectedSubcategories, subcategoryOptions]);

    const frequencyOptions = useMemo<FilterOption[]>(
        () => [
            {
                value: AdFrequency.ONE_TIME,
                label: String(intl.formatMessage("ad.card.frequency_one_time")),
            },
            {
                value: AdFrequency.RECURRING,
                label: String(intl.formatMessage("ad.card.frequency_recurring")),
            },
        ],
        [intl]
    );

    const postedPeriodOptions = useMemo<FilterOption[]>(
        () => [
            {
                value: POSTED_PERIOD_LAST_24H,
                label: String(intl.formatMessage("ad.list.filters.posted_last_24h")),
            },
            {
                value: POSTED_PERIOD_LAST_WEEK,
                label: String(intl.formatMessage("ad.list.filters.posted_last_week")),
            },
            {
                value: POSTED_PERIOD_LAST_MONTH,
                label: String(intl.formatMessage("ad.list.filters.posted_last_month")),
            },
        ],
        [intl]
    );
    const postedPeriodClearOption = useMemo<FilterOption>(
        () => ({
            value: POSTED_PERIOD_ALL,
            label: String(intl.formatMessage("ad.list.filters.posted_all")),
        }),
        [intl]
    );

    useEffect(() => {
        setCityInput(selectedCity);
    }, [selectedCity]);

    useEffect(() => {
        if (cityInput.trim().length < 3 || cityInput === selectedCity) {
            setCityOptions([]);
            return;
        }

        let request: CancellablePromise<HttpResponse<unknown>> | undefined;
        const timeout = setTimeout(() => {
            request = fetchAutocompleteAddress(
                {
                    q: cityInput,
                    limit: 8,
                },
                "municipality"
            );

            request.then((response) => {
                const features = ((response.data as { features?: CityFeature[] }).features ?? []);
                const uniqueOptions = new Map<string, CityOption>();

                features.forEach((feature) => {
                    const city = feature.properties.city || feature.properties.label;
                    const [lon, lat] = feature.geometry.coordinates;
                    const label = feature.properties.context
                        ? `${feature.properties.label} - ${feature.properties.context}`
                        : feature.properties.label;
                    uniqueOptions.set(`${city}-${lat}-${lon}`, {
                        value: city,
                        label,
                        lat,
                        lon,
                    });
                });

                setCityOptions(Array.from(uniqueOptions.values()));
            });
        }, 300);

        return () => {
            clearTimeout(timeout);
            request?.cancel();
        };
    }, [cityInput, selectedCity]);

    const applyFilters = useCallback(
        (nextFilters: HashMap<unknown>) => {
            const params = buildSearchParams({
                activeFilters: {
                    ...activeFilters,
                    ...queryValues,
                    ...nextFilters,
                },
                keyword: currentKeyword,
                pager,
            });

            changeFetcherParams(params);
            setAll(params);
        },
        [changeFetcherParams, currentKeyword, activeFilters, pager, queryValues, setAll]
    );

    const handleCategoryChange = useCallback(
        (categoriesValue: string[]) => {
            const selectedCategoriesValueSet = new Set(categoriesValue);
            const allowedSubcategories = new Set(
                categories
                    .filter(
                        (category) =>
                            selectedCategoriesValueSet.size === 0 || selectedCategoriesValueSet.has(category.value)
                    )
                    .flatMap((category) => category.subcategories.map((subcategory) => subcategory.value))
            );

            applyFilters({
                filter_categories: joinCsv(categoriesValue),
                filter_subcategories: joinCsv(
                    selectedSubcategories.filter((subcategory) => allowedSubcategories.has(subcategory))
                ),
            });
        },
        [applyFilters, categories, selectedSubcategories]
    );

    const handleSubcategoryChange = useCallback(
        (subcategories: string[]) => applyFilters({ filter_subcategories: joinCsv(subcategories) }),
        [applyFilters]
    );

    const handleFrequencyChange = useCallback(
        (frequencies: string[]) =>
            applyFilters({
                filter_frequencies: frequencies.length === 1 ? joinCsv(frequencies) : undefined,
            }),
        [applyFilters]
    );

    const handlePostedPeriodChange = useCallback(
        (value: string) =>
            applyFilters({
                filter_posted_period: value === POSTED_PERIOD_ALL ? undefined : value,
            }),
        [applyFilters]
    );

    const handleRadiusChange = useCallback(
        (value: string) =>
            applyFilters({
                filter_range: value === RADIUS_ALL ? undefined : value,
            }),
        [applyFilters]
    );

    const handleCityInputChange = useCallback(
        (value: string) => {
            setCityInput(value);

            if (value.trim() === "") {
                setCityOptions([]);
                applyFilters({
                    filter_city: undefined,
                    filter_coordinates_lat: undefined,
                    filter_coordinates_lon: undefined,
                });
            }
        },
        [applyFilters]
    );

    const handleCitySelect = useCallback(
        (option: CityOption) => {
            setCityInput(option.value);
            setCityOptions([]);
            applyFilters({
                filter_city: option.value,
                filter_coordinates_lat: option.lat,
                filter_coordinates_lon: option.lon,
            });
        },
        [applyFilters]
    );

    const handleReset = useCallback(() => {
        const params = buildResetInlineAdFilterSearchParams({
            currentQueryValues: queryValues,
            pager,
        });

        changeFetcherParams(params);
        setAll(params);
    }, [changeFetcherParams, pager, queryValues, setAll]);

    return {
        labels: {
            category: String(intl.formatMessage("ad.list.filters.category")),
            subcategory: String(intl.formatMessage("ad.list.filters.subcategory")),
            frequency: String(intl.formatMessage("ad.list.filters.frequency")),
            posted: String(intl.formatMessage("ad.list.filters.posted")),
            city: String(intl.formatMessage("ad.list.filters.city")),
            radius: String(intl.formatMessage("ad.list.filters.radius")),
            radiusAll: String(intl.formatMessage("ad.list.filters.radius_all")),
            radiusValue: (radius: number) =>
                String(intl.formatMessage("ad.list.filters.radius_kilometers", { radius })),
            selectedCount: (count: number) =>
                String(intl.formatMessage("ad.list.filters.selected_count", { count })),
            reset: intl.formatMessage("ad.list.filters.reset"),
        },
        options: {
            categories: categoryOptions,
            subcategories: subcategoryOptions,
            frequencies: frequencyOptions,
            postedPeriods: postedPeriodOptions,
            postedPeriodClear: postedPeriodClearOption,
            cities: cityOptions,
        },
        selected: {
            categories: selectedCategories,
            subcategories: displayedSubcategories,
            frequencies: selectedFrequencies,
            postedPeriod: selectedPostedPeriod || POSTED_PERIOD_ALL,
            radius: selectedRadius,
            city: cityInput,
        },
        state: {
            canUseRadius,
            hasInlineAdFilters,
        },
        handlers: {
            handleCategoryChange,
            handleSubcategoryChange,
            handleFrequencyChange,
            handlePostedPeriodChange,
            handleRadiusChange,
            handleCityInputChange,
            handleCitySelect,
            handleReset,
        },
    };
};
